import { useState, useEffect } from "react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotifications, UserNotification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";

const notificationTypeIcons: Record<string, string> = {
  nft_approved: "✅",
  nft_rejected: "",
  nft_sold: "",
  nft_purchased: "",
  sale_milestone: "",
  admin_alert_wash_trade: "",
  badge_earned: "",
};

const notificationTypeColors: Record<string, string> = {
  nft_approved: "border-l-green-500",
  nft_rejected: "border-l-red-500",
  nft_sold: "border-l-green-500",
  nft_purchased: "border-l-cyan-500",
  sale_milestone: "border-l-yellow-500",
  admin_alert_wash_trade: "border-l-orange-500",
  badge_earned: "border-l-purple-500",
};

function NotificationItem({
  notification,
  onMarkRead,
}: {
  notification: UserNotification;
  onMarkRead: (id: string) => void;
}) {
  const icon = notificationTypeIcons[notification.notification_type] || "📢";
  const colorClass = notificationTypeColors[notification.notification_type] || "border-l-primary";

  return (
    <div
      className={`p-3 border-l-4 ${colorClass} ${
        notification.is_read ? "bg-muted/30 opacity-70" : "bg-card"
      } rounded-r-lg mb-2 transition-all hover:bg-muted/50`}
    >
      <div className="flex items-start gap-2">
        <span className="text-lg">{icon}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-sm text-foreground truncate">
              {notification.title}
            </p>
            {!notification.is_read && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => onMarkRead(notification.id)}
              >
                <Check className="h-3 w-3" />
              </Button>
            )}
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {notification.message}
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </p>
        </div>
      </div>
    </div>
  );
}

export function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);

  // Mark all as read when popover opens
  useEffect(() => {
    if (open && unreadCount > 0) {
      markAllAsRead();
    }
  }, [open, unreadCount, markAllAsRead]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-foreground hover:text-primary">
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium animate-pulse">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b border-border">
          <h4 className="font-semibold text-sm">Notifications</h4>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={markAllAsRead}
            >
              <CheckCheck className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          <div className="p-2">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={markAsRead}
                />
              ))
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
